import { PaymentElement } from "@stripe/react-stripe-js";
import React from "react";
import styled from "styled-components";
import type { StripePaymentElementChangeEvent } from "@stripe/stripe-js";
import type Stripe from "stripe";

import CheckoutController, { CheckoutControllerState } from "js/controllers/CheckoutController";
import { t } from "js/i18n";
import { CreditCardIcon } from "js/react/views/UserOptions/panes/BillingPane/Components/CreditCardIcon";
import { LinkByStripeIcon } from "js/react/views/UserOptions/panes/BillingPane/Components/LinkByStripeIcon";

const PaymentFormContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-top: 6px;
`;

const DefaultPaymentMethodContainer = styled.div<{ $dimmed?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    min-height: 44px;
    padding: 10px 12px;
    border: 1px solid #DADCE0;
    border-radius: 4px;
    color: #222;
    font-family: "Source Sans Pro";
    font-size: 14px;
    line-height: 125%;
    margin-top: 10px;
    background: ${({ $dimmed }) => $dimmed ? "#F7F8FA" : "transparent"};
`;

const DefaultPaymentMethodDetails = styled.div<{ $dimmed?: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    opacity: ${({ $dimmed }) => $dimmed ? 0.55 : 1};
`;

const DefaultPaymentMethodActionButton = styled.button<{ disabled?: boolean }>`
    flex-shrink: 0;
    border: none;
    background: none;
    padding: 0;
    color: #11A9E2;
    font-family: "Source Sans Pro";
    font-size: 14px;
    font-weight: 600;
    line-height: 125%;
    cursor: pointer;
    pointer-events: ${({ disabled }) => disabled ? "none" : "auto"};
`;

export function DefaultPaymentMethod({ paymentMethod, actionLabel, onAction, dimmed, disabled }: {
    paymentMethod: Stripe.PaymentMethod;
    actionLabel: string;
    onAction: () => void;
    dimmed?: boolean;
    disabled?: boolean;
}) {
    const ActionButton = (
        <DefaultPaymentMethodActionButton
            type="button"
            onClick={onAction}
            disabled={disabled}
        >
            {actionLabel}
        </DefaultPaymentMethodActionButton>
    );

    if (paymentMethod.card) {
        return (
            <DefaultPaymentMethodContainer $dimmed={dimmed}>
                <DefaultPaymentMethodDetails $dimmed={dimmed}>
                    <CreditCardIcon small card={paymentMethod.card} />
                    <div data-test-id="payment-method-card-info">****{paymentMethod.card.last4}</div>
                </DefaultPaymentMethodDetails>
                {ActionButton}
            </DefaultPaymentMethodContainer>
        );
    }

    if (paymentMethod.type === "link") {
        return (
            <DefaultPaymentMethodContainer $dimmed={dimmed}>
                <DefaultPaymentMethodDetails $dimmed={dimmed}>
                    <LinkByStripeIcon />
                    <div data-test-id="payment-method-link-info">{paymentMethod.link?.email ?? t("Link by Stripe")}</div>
                </DefaultPaymentMethodDetails>
                {ActionButton}
            </DefaultPaymentMethodContainer>
        );
    }

    return (
        <DefaultPaymentMethodContainer $dimmed={dimmed} data-test-id="payment-method-card-info">
            <DefaultPaymentMethodDetails $dimmed={dimmed}>
                {t("Payment information missing")}
            </DefaultPaymentMethodDetails>
            {ActionButton}
        </DefaultPaymentMethodContainer>
    );
}

export const PaymentForm = CheckoutController.withState(function PaymentForm({ createPaymentMethod, fetching }: CheckoutControllerState) {
    const handlePaymentElementChange = (event: StripePaymentElementChangeEvent) => {
        CheckoutController.setPaymentElementState({ complete: event.complete });
    };

    const handleUseNewPaymentMethod = async () => {
        await CheckoutController.setCreatePaymentMethod(true);
    };

    const handleUseDefaultPaymentMethod = async () => {
        await CheckoutController.setCreatePaymentMethod(false);
    };

    const defaultPaymentMethod = CheckoutController.defaultPaymentMethod;

    if (defaultPaymentMethod && !createPaymentMethod) {
        return (
            <PaymentFormContainer>
                <DefaultPaymentMethod
                    paymentMethod={defaultPaymentMethod}
                    actionLabel={t("Use different card")}
                    onAction={handleUseNewPaymentMethod}
                    disabled={fetching}
                />
            </PaymentFormContainer>
        );
    }

    return (
        <PaymentFormContainer>
            {defaultPaymentMethod &&
                <DefaultPaymentMethod
                    paymentMethod={defaultPaymentMethod}
                    actionLabel={t("Use this card")}
                    onAction={handleUseDefaultPaymentMethod}
                    dimmed
                    disabled={fetching}
                />
            }
            <PaymentElement
                onChange={handlePaymentElementChange}
                options={{
                    layout: {
                        type: "tabs",
                        defaultCollapsed: false,
                        radios: false,
                        spacedAccordionItems: false
                    },
                    wallets: {
                        applePay: "auto",
                        googlePay: "never",
                        link: "auto"
                    },
                    paymentMethodOrder: ["card", "applePay", "link"],
                    terms: {
                        card: "never"
                    },
                    fields: {
                        billingDetails: {
                            address: {
                                country: "never",
                                postalCode: "never"
                            }
                        }
                    }
                }}
            />
        </PaymentFormContainer>
    );
});
